import * as ko from 'knockout';
import * as _ from 'underscore';
import * as localisationHelper from 'localisationHelper';

// uidExtender and bindings
import "@legend/knockout/binding/id";
import "@legend/knockout/extender/uid";
import type { ObservableWithUid } from "@legend/knockout/extender/uid";

// Services
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";
import { CountryResponseModel } from 'Content/ts/Models/Shared/AddressModels';
import { DefaultSelectTwoModel } from "Content/ts/Models/Shared/SelectTwo/selectTwoModels";
import { CountryService } from "Content/ts/Shared/Services/Country/countryService";

// Components
import "Content/ts/Shared/BootstrapAlerts/AlertValidation/alert-validation";

export const Deps = {
    ValidationService: ValidationService
}

export interface ICountrySelectorParams {
    SelectedCountry: ObservableWithUid<string>;
    IsRequired: boolean | KnockoutObservable<boolean>;                                  // Optional
    ValidationFields: KnockoutObservable<any>[];                                        // Conditional - Some components may need this in order to function
    IsReadOnly: boolean | KnockoutObservable<boolean>;
}

export default class CountrySelectorViewModel {
    isLoading: KnockoutObservable<boolean>;
    countries: KnockoutObservableArray<DefaultSelectTwoModel>;
    selectedCountry: ObservableWithUid<string>;
    isRequired: boolean | KnockoutObservable<boolean>;
    validationFields: KnockoutObservable<any>[];
    IsReadOnly: boolean | KnockoutObservable<boolean>;

    customMessages: {
        CountryLabelText: string;
        SelectCountryText: string;
        LoadingCountries: string;
        CountryValidationText: string;
    }

    constructor(params: ICountrySelectorParams) {
        this.isLoading = ko.observable<boolean>(false);
        this.countries = ko.observableArray<DefaultSelectTwoModel>([]);

        this.selectedCountry = params.SelectedCountry;
        this.isRequired = params.IsRequired || false;
        this.IsReadOnly = params.IsReadOnly || false;
        this.validationFields = params.ValidationFields || [];

        this.selectedCountry.extend({ uid: 'CountrySelector' });

        this.getCustomMessages();
        this.setupValidation();
        this.createSubscriptions();

        this.getCountries();
    }

    private setupValidation(): void {
        Deps.ValidationService.AddValidationRule(this.selectedCountry, this.validationFields, {
            required: {
                params: ko.unwrap(this.isRequired),
                message: this.customMessages.CountryValidationText
            },
            validation: {
                validator: (value: string) => {
                    return this.countries().find(c => c.text === value);
                },
                message: this.customMessages.CountryValidationText,
                onlyIf: () => {
                    return ko.unwrap(this.isRequired) && !ko.unwrap(this.IsReadOnly);
                }
            }
        });
    };

    getCustomMessages() {
        this.customMessages = {
            CountryLabelText: localisationHelper.getString("AccountResources", "Register_Country"),
            SelectCountryText: localisationHelper.getString("AccountResources", "Register_SelectCountry"),
            LoadingCountries: localisationHelper.getString("GenericResources", "LoadingCountries"),
            CountryValidationText: localisationHelper.getString("AccountResources", "Register_Validation_CountryRequired")
        }
    };

    getCountries = () => {
        this.isLoading(true);
        return CountryService.GetCountries()
            .then((response: CountryResponseModel[]) => {
                this.updateCountries(response.map(c => <DefaultSelectTwoModel>{ id: c.Name, text: c.Name }));
                this.isLoading(false);
            });
    };

    createSubscriptions = () => {
        this.selectedCountry.subscribe((newVal) => {
            if (newVal) {
                this.updateCountries();
            }
        });
    };

    updateCountries(countries: DefaultSelectTwoModel[] = null) {
        countries = countries || this.countries();
        if (this.selectedCountry()) {
            var selectedCountry = countries.find(c => c.text == this.selectedCountry());
            if (selectedCountry) {
                this.countries().filter(c => c.selected).forEach(c => c.selected = false);
                selectedCountry.selected = true;
            }
        }

        this.countries(countries);
    }

    dispose() {
        Deps.ValidationService.RemoveValidationRule(this.selectedCountry, this.validationFields);
        delete this.selectedCountry.uid;
    }
}

const componentName = "country-selector";
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: CountrySelectorViewModel,
    template: { require: `text!/Themes/Default/Content/ts/Shared/Address/${componentName}.html` }
});